import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '@/store/hooks'
import { api } from '@/store/api'
import { attachJob } from '@/features/ai/jobsSlice'
import { addUserMessage } from '@/features/ai/chatSlice'
import { useStartCourseGenerationMutation } from '@/features/ai/studioApi'
import { parseStartError, type StartFailure } from '@/features/ai/lib/parseStartError'
import { MAX_PROMPT_LENGTH } from '@/features/ai/lib/promptLimits'
import type { Lang } from '@/types/lang'

/** Ровно три допустимых значения: иное бэк отбивает Pydantic-422 без текста. */
export type LessonCount = 3 | 5 | 10

/**
 * Ветки старта взаимоисключающи ПО ТИПУ, а не по комментарию (M-3): с
 * `courseId` уроки ДОПИСЫВАЮТСЯ в существующий курс, и поле `language` бэк там
 * игнорирует — берёт `sourceLang` курса. Раньше вызывающий из сайдбара
 * передавал туда литерал `'ru'`, который читался как решение «всегда русский»,
 * хотя не уходил в сеть вовсе.
 */
export type StartInput = { prompt: string; lessonCount: LessonCount } & (
  { courseId: number; language?: never } | { courseId?: undefined; language: Lang }
)

export const useStartGeneration = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [startGeneration, { isLoading }] = useStartCourseGenerationMutation()
  const [failure, setFailure] = useState<StartFailure | null>(null)

  /**
   * Единственный вход в старт генерации (лендинг и AI-сайдбар), поэтому и
   * валидация промпта живёт здесь, а не в каждом экране. Возвращает `true`
   * только при реально стартовавшем прогоне: вызвавший по нему решает, можно
   * ли чистить поле ввода (при отказе текст автора терять нельзя).
   */
  const start = useCallback(
    async (input: StartInput): Promise<boolean> => {
      setFailure(null)
      const prompt = input.prompt.trim()
      // Пустой/переросший промпт — не отказ сервера, а «ничего не произошло»:
      // `failure` не выставляем (на лендинге кнопка и так погашена).
      if (!prompt || prompt.length > MAX_PROMPT_LENGTH) return false
      try {
        const started = await startGeneration({
          prompt,
          lesson_count: input.lessonCount,
          // В ветке с курсом language НЕ шлётся: бэк берёт язык из sourceLang
          // курса и это поле там игнорирует — не обещаем выбор, которого нет.
          ...(input.courseId ? { course_id: input.courseId } : { language: input.language }),
        }).unwrap()

        // Дописываем в курс, где уже был прогон: `attachJob` ниже сам чистит
        // историю прошлого прогона (инвариант живёт в `chatSlice`, см. I-1 —
        // иначе каждый вызывающий `attach` должен был бы помнить про вторую
        // строчку `detach()`). Только после успешного ответа — упавший старт
        // не имеет права стереть итог, который автор ещё не прочитал.
        dispatch(
          attachJob({
            courseId: input.courseId ?? started.course_id,
            entry: {
              jobId: started.job_id,
              type: 'course_generation',
              startedAt: Date.now(),
              // Джоба не возвращает параметры запуска — храним у себя (§1.2).
              params: {
                kind: 'course_generation',
                prompt,
                lesson_count: input.lessonCount,
              },
              // Знаменатель прогресса до первого колбэка: result ещё пуст.
              total: input.lessonCount,
            },
          }),
        )
        dispatch(
          addUserMessage({
            courseId: input.courseId ?? started.course_id,
            // aiChat персистится (redux-persist), а модульный счётчик — нет:
            // после F5 он обнулился бы и родил дубликат id, который React
            // использует как key (см. CLAUDE.md про key-коллизии).
            message: { id: crypto.randomUUID(), text: prompt, at: Date.now() },
          }),
        )
        navigate(`/editor/${input.courseId ?? started.course_id}`)
        return true
      } catch (error) {
        const parsed = parseStartError(error)
        setFailure(parsed)
        // §4.7: в редкой гонке (двойной клик) черновик УЖЕ закоммичен, а его
        // id в теле `400` не приходит. Инвалидация списка — единственный
        // способ показать сироту в мастерской, откуда его можно удалить.
        if (parsed.kind === 'no_credits') {
          dispatch(api.util.invalidateTags([{ type: 'Course', id: 'LIST' }]))
        }
        return false
      }
    },
    [startGeneration, dispatch, navigate],
  )

  return { start, isStarting: isLoading, failure, reset: () => setFailure(null) }
}
